/**
 * Rich card for quote manager-approval DM messages.
 */

import React, { useMemo } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { colorTokens } from "@/widgets/frontdesk/design-system";
import { buildQuoteUrl } from "@/widgets/quote-new/buttons-container/build-quote-approval-message";
import { useOpenQuoteLikeList } from "../hooks/open-quote-like-list";

export type QuoteApprovalMeta = {
  quoteId?: string;
  quoteNumber?: string;
  clientName?: string;
  totalPrice?: string;
  quoteUrl?: string;
};

export type QuoteApprovalResultMeta = {
  outcome: "approved" | "rejected";
  quoteNumber?: string;
};

export function getQuoteApprovalMeta(
  message: { metadata?: unknown; content?: string } | null | undefined,
): QuoteApprovalMeta | null {
  const meta = message?.metadata as
    | {
        type?: string;
        quoteApproval?: QuoteApprovalMeta;
      }
    | undefined;
  if (meta?.type === "quote_approval" && meta.quoteApproval) {
    return meta.quoteApproval;
  }
  return null;
}

export function getQuoteApprovalResultMeta(
  message: { metadata?: unknown; content?: string } | null | undefined,
): QuoteApprovalResultMeta | null {
  const meta = message?.metadata as
    | {
        type?: string;
        outcome?: string;
        quoteNumber?: string;
      }
    | undefined;
  if (meta?.type !== "quote_approval_result") return null;
  if (meta.outcome !== "approved" && meta.outcome !== "rejected") return null;
  return {
    outcome: meta.outcome,
    quoteNumber: meta.quoteNumber,
  };
}

/** Compact approved / rejected status line for DM bubbles. */
export const QuoteApprovalResultMessage: React.FC<{
  data: QuoteApprovalResultMeta;
  content?: string;
}> = ({ data, content }) => {
  const { t } = useTranslation();
  const approved = data.outcome === "approved";
  const number = data.quoteNumber || "N/A";
  const label =
    content?.trim() ||
    (approved
      ? t("sales.quote.quoteApprovalManagerApproved", {
          number,
          defaultValue: `Quote ${number} was approved by the manager.`,
        })
      : t("sales.quote.quoteApprovalManagerRejected", {
          number,
          defaultValue: `Quote ${number} was rejected by the manager.`,
        }));

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1}
      sx={{ direction: "ltr", textAlign: "start" }}
    >
      {approved ? (
        <CheckCircleIcon
          sx={{ fontSize: 20, color: "#16A34A", mt: "1px", flexShrink: 0 }}
        />
      ) : (
        <CancelIcon
          sx={{ fontSize: 20, color: "#DC2626", mt: "1px", flexShrink: 0 }}
        />
      )}
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          color: approved ? "#15803D" : "#B91C1C",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          lineHeight: 1.45,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

export const QuoteApprovalMessageCard: React.FC<{
  data: QuoteApprovalMeta;
  compact?: boolean;
}> = ({ data, compact = false }) => {
  const { t } = useTranslation();
  const { requestOpenQuote, quoteOpenModal } = useOpenQuoteLikeList();
  // Always open on the viewer's current origin (not the sender's stored domain).
  const quoteUrl = useMemo(() => {
    if (data.quoteId) return buildQuoteUrl(data.quoteId);
    return data.quoteUrl || "";
  }, [data.quoteId, data.quoteUrl]);
  const quoteId = data.quoteId || quoteIdFromUrl(quoteUrl);

  return (
    <Box
      sx={{
        mt: 0.5,
        p: compact ? 1.5 : 1.75,
        borderRadius: "12px",
        border: `1px solid ${colorTokens.neutral[200]}`,
        backgroundColor: "#fff",
        minWidth: compact ? 200 : 240,
        maxWidth: "100%",
        position: "relative",
        // Keep text clear of the document icon on the inline-start side
        paddingInlineStart: compact ? "56px" : "60px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          insetInlineStart: compact ? 12 : 14,
          top: compact ? 12 : 14,
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(46, 48, 146, 0.08)",
          color: "var(--theme-primary-color, #2E3092)",
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: colorTokens.neutral[900],
          mb: 0.75,
          lineHeight: 1.4,
        }}
      >
        {t("sales.quote.quoteApprovalRequestTitle", {
          defaultValue: "Quote Approval Request",
        })}
      </Typography>

      <Stack spacing={0.35}>
        <Typography sx={{ fontSize: "0.8125rem", color: colorTokens.neutral[700] }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t("sales.quote.quoteApprovalQuoteNumber", {
              defaultValue: "Quote Number",
            })}
            :
          </Box>{" "}
          {data.quoteNumber || "N/A"}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: colorTokens.neutral[700] }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t("sales.quote.quoteApprovalClientName", {
              defaultValue: "Client Name",
            })}
            :
          </Box>{" "}
          {data.clientName || "N/A"}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: colorTokens.neutral[700] }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {t("sales.quote.quoteApprovalTotalPrice", {
              defaultValue: "Total Price",
            })}
            :
          </Box>{" "}
          {data.totalPrice || "N/A"}
        </Typography>
      </Stack>
      <Typography
        sx={{
          mt: 1,
          fontSize: "0.8125rem",
          color: colorTokens.neutral[600],
          lineHeight: 1.45,
        }}
      >
        {t("sales.quote.quoteApprovalPleaseReview", {
          defaultValue: "Please review and approve this quote.",
        })}
      </Typography>
      {quoteId ? (
        <Button
          size="small"
          variant="contained"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void requestOpenQuote(quoteId);
          }}
          sx={{
            mt: 1.25,
            textTransform: "none",
            borderRadius: "8px",
            px: 1.5,
            py: 0.75,
            backgroundColor: "var(--theme-primary-color, #2E3092)",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "var(--theme-primary-color, #2E3092)",
              opacity: 0.92,
              boxShadow: "none",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ direction: "ltr" }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            <Box component="span">
              {t("sales.quote.quoteApprovalOpenQuote", {
                defaultValue: "Open quote",
              })}
            </Box>
          </Stack>
        </Button>
      ) : null}
      {quoteOpenModal}
    </Box>
  );
};

function quoteIdFromUrl(url: string): string {
  if (!url) return "";
  try {
    return new URL(url, "http://localhost").searchParams.get("Id") || "";
  } catch (_error) {
    return "";
  }
}
